/**
 * @fileoverview Centralized cache registry for data source caches. Provides a
 * single source of truth for cache instances, preventing duplicate caches and
 * enabling cross-entity cache invalidation.
 */

import { DataSourceCache, DataSourceCacheConfig } from './cache';

/**
 * Known entity types for type-safe cache access.
 * Extend this union type when adding new entity data sources.
 */
export type EntityType =
  | 'account'
  | 'ai_prompt'
  | 'area'
  | 'attachment'
  | 'calorie'
  | 'category'
  | 'embedding'
  | 'history'
  | 'kind'
  | 'login'
  | 'note'
  | 'note_tag'
  | 'note_visit'
  | 'organisation'
  | 'person'
  | 'project'
  | 'resource'
  | 'speed_dial_item'
  | 'status'
  | 'subcategory'
  | 'tag'
  | 'weight';

/**
 * Singleton registry holding all data source caches.
 */
const registry: Map<EntityType | string, DataSourceCache> = new Map();

/**
 * Checks if a cache exists for the specified entity type without creating one.
 *
 * @param entityType - The entity type to check
 * @returns true if a cache exists for this entity type
 */
export function hasCache(entityType: EntityType): boolean {
  return registry.has(entityType);
}

/**
 * Gets or creates a cache for the specified entity type.
 * Returns the same cache instance for repeated calls with the same key.
 *
 * @param entityType - The entity type identifier (e.g., 'note', 'tag')
 * @param config - Optional cache configuration (only used when creating new cache)
 * @returns DataSourceCache instance for the entity type
 */
export function getCache(entityType: EntityType, config?: DataSourceCacheConfig): DataSourceCache {
  const existing = registry.get(entityType);
  if (existing) {
    return existing;
  }

  const cache = new DataSourceCache(config);
  registry.set(entityType, cache);
  return cache;
}

/**
 * Removes a cache from the registry entirely.
 * Use this for cleanup when an entity type is no longer needed.
 *
 * @param entityType - The entity type to remove cache for
 * @returns true if a cache was removed, false if it didn't exist
 */
export function removeCache(entityType: EntityType): boolean {
  const cache = registry.get(entityType);
  if (cache) {
    cache.clear();
    return registry.delete(entityType);
  }
  return false;
}

/**
 * Clears a specific entity's cache entries without removing from registry.
 *
 * @param entityType - The entity type to clear cache for
 */
export function clearCache(entityType: EntityType): void {
  const cache = registry.get(entityType);
  if (cache) {
    cache.clear();
  }
}

/**
 * Clears all caches in the registry.
 * Useful for logout or full data refresh scenarios.
 */
export function clearAllCaches(): void {
  registry.forEach((cache) => cache.clear());
}

/**
 * Gets all registered entity types with active caches.
 */
export function getRegisteredEntityTypes(): EntityType[] {
  return Array.from(registry.keys()) as EntityType[];
}
